import React, { useState, useCallback } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  X, FileArchive, Send, Download,
} from 'lucide-react';

import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import useLayoutBucket from '../../../hooks/useLayoutBucket';
import { drawerGeometry } from '../../../components/layout/shellMetrics';
import { formatDateTime } from '../../../utils/Utils';
import { BULK_KIND, isActiveJob, jobProgress } from '../config/bulk';

// Kullanıcının toplu işleri — SALT OKUNUR panel. Neden GenericFormDrawer DEĞİL: bu
// bir form değil (OfferApprovalChainDrawer ile aynı gerekçe); kabuk ölçüleri o
// çekmeceyle aynıdır, genişlik kırılım kovasından gelir (FE-08, drawerGeometry).
//
// Durum yoklaması çekmecede DEĞİL sayfadadır (useBulkJobs) — rozet ve tablo
// tazelemesi çekmece kapalıyken de çalışsın diye. Burası yalnız çizer.

const STATUS_COLORS = {
  queued: { color: '#64748B', bg: '#F1F5F9' },
  running: { color: '#0270BA', bg: '#E0F2FE' },
  succeeded: { color: '#16A34A', bg: '#DCFCE7' },
  failed: { color: '#DC2626', bg: '#FEE2E2' },
  canceled: { color: '#92400E', bg: '#FEF3C7' },
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  margin: 0;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

// TEK KAYDIRMA: gövde kendi içinde kayar; `min-height: 0` atlanırsa taşma sayfaya döner.
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Note = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Notice = styled.div`
  font-size: 13px;
  line-height: 1.5;
  border-radius: 6px;
  padding: 10px 12px;
  background: ${({ $tone }) => ($tone === 'danger' ? '#FEF2F2' : '#F5F7F9')};
  border: 1px solid ${({ $tone }) => ($tone === 'danger' ? '#FECACA' : '#E8ECF0')};
  color: ${({ $tone }) => ($tone === 'danger' ? '#B3261E' : '#475569')};
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const KindIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  color: #fff;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary.main}, ${({ theme }) => theme.colors.secondary.main});
`;

const CardTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusChip = styled.span`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $c }) => $c.color};
  background: ${({ $c }) => $c.bg};
`;

const Track = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #EDF1F4;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 3px;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary.main}, ${({ theme }) => theme.colors.secondary.main});
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};

  strong { color: ${({ theme }) => theme.colors.text.primary}; font-weight: 600; }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const Empty = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export default function BulkJobsDrawer({
  open, onClose, jobs, loading, failed, onRetry, onDownload, onCancel,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const geometry = drawerGeometry(useLayoutBucket());
  const [cancelTarget, setCancelTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = useCallback(async (job) => {
    setDownloadingId(job.id);
    await onDownload(job);
    setDownloadingId(null);
  }, [onDownload]);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelTarget) return;
    setCanceling(true);
    await onCancel(cancelTarget);
    setCanceling(false);
    setCancelTarget(null);
  }, [cancelTarget, onCancel]);

  const renderJob = (job) => {
    const isZip = job.kind === BULK_KIND.ZIP;
    const active = isActiveJob(job);
    const statusKey = active && job.cancelRequested ? 'canceling' : job.status;
    const colors = STATUS_COLORS[job.status] || STATUS_COLORS.queued;
    const expired = job.status === 'succeeded' && !job.hasResult;
    // İptali zaten istenmiş iş ikinci kez iptal edilmez (sunucu kabul eder ama anlamsız).
    const canCancel = active && !job.cancelRequested;

    return (
      <Card key={job.id} data-role={`bulk-job-${job.id}`}>
        <CardHead>
          <KindIcon aria-hidden="true">{isZip ? <FileArchive size={16} /> : <Send size={16} />}</KindIcon>
          <CardTitle>
            {t(isZip ? 'leaveReconciliation.bulk.kindZip' : 'leaveReconciliation.bulk.kindNotify', { year: job.year })}
          </CardTitle>
          <StatusChip $c={colors} data-role="bulk-job-status">
            {t(`leaveReconciliation.bulk.jobStatus.${statusKey}`, { defaultValue: job.status })}
          </StatusChip>
        </CardHead>

        {(active || job.processedCount > 0) && (
          <>
            <Track role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={jobProgress(job)}>
              <Fill $pct={jobProgress(job)} />
            </Track>
            <Meta>
              <span data-role="bulk-job-progress"><strong>{job.processedCount}</strong> / {job.totalCount}</span>
              <span>
                {t(isZip ? 'leaveReconciliation.bulk.countIncluded' : 'leaveReconciliation.bulk.countNotified')}
                {': '}
                <strong>{job.succeededCount}</strong>
              </span>
              <span>
                {t('leaveReconciliation.bulk.countSkipped')}
                {': '}
                <strong>{job.skippedCount}</strong>
              </span>
              {job.failedCount > 0 && (
                <span>
                  {t('leaveReconciliation.bulk.countFailed')}
                  {': '}
                  <strong>{job.failedCount}</strong>
                </span>
              )}
            </Meta>
          </>
        )}

        <Meta>
          <span>{t('leaveReconciliation.bulk.enqueuedAt')}: {formatDateTime(job.enqueuedAt, lang)}</span>
          {job.finishedAt && (
            <span>{t('leaveReconciliation.bulk.finishedAt')}: {formatDateTime(job.finishedAt, lang)}</span>
          )}
        </Meta>

        {job.error && (
          <Notice $tone="danger">
            {t(`responseErrors.${job.error}`, { defaultValue: t('responseErrors.leaveReconciliation.bulkJobFailed') })}
          </Notice>
        )}

        {(job.hasResult || expired || canCancel) && (
          <Actions>
            {job.hasResult && (
              <>
                <Button
                  variant="outline"
                  data-role="bulk-job-download"
                  loading={downloadingId === job.id}
                  onClick={() => handleDownload(job)}
                >
                  <Download size={14} />
                  {' '}
                  {t(isZip ? 'leaveReconciliation.bulk.downloadZip' : 'leaveReconciliation.bulk.downloadReport')}
                </Button>
                {job.resultExpiresAt && (
                  <Note>{t('leaveReconciliation.bulk.expiresAt', { date: formatDateTime(job.resultExpiresAt, lang) })}</Note>
                )}
              </>
            )}
            {expired && <Note data-role="bulk-job-expired">{t('leaveReconciliation.bulk.resultExpired')}</Note>}
            {canCancel && (
              <Button variant="ghost" data-role="bulk-job-cancel" onClick={() => setCancelTarget(job)}>
                {t('leaveReconciliation.bulk.cancel')}
              </Button>
            )}
          </Actions>
        )}
      </Card>
    );
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        direction="right"
        size={geometry.size}
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: geometry.minWidth,
          margin: geometry.margin,
          borderRadius: geometry.borderRadius,
          height: geometry.height,
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
        }}
      >
        <Header>
          <Title>{t('leaveReconciliation.bulk.jobsTitle')}</Title>
          <CloseBtn onClick={onClose} aria-label={t('common.close')}><X size={20} /></CloseBtn>
        </Header>
        <Body data-role="bulk-jobs">
          <Note>{t('leaveReconciliation.bulk.jobsNote')}</Note>

          {failed && (
            <Notice $tone="danger">
              {t('leaveReconciliation.bulk.jobsLoadFailed')}
              {' '}
              <Button variant="ghost" onClick={onRetry}>{t('leaveReconciliation.bulk.retry')}</Button>
            </Notice>
          )}

          {loading && jobs.length === 0 && <Empty>{t('common.loading')}</Empty>}
          {!loading && !failed && jobs.length === 0 && (
            <Empty data-role="bulk-jobs-empty">{t('leaveReconciliation.bulk.jobsEmpty')}</Empty>
          )}

          {jobs.map(renderJob)}
        </Body>
      </Drawer>

      <ConfirmDialog
        isOpen={!!cancelTarget}
        title={t('leaveReconciliation.bulk.cancelTitle')}
        message={cancelTarget?.kind === BULK_KIND.NOTIFY
          ? t('leaveReconciliation.bulk.cancelMessageNotify')
          : t('leaveReconciliation.bulk.cancelMessageZip')}
        confirmText={t('leaveReconciliation.bulk.cancel')}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelTarget(null)}
        loading={canceling}
      />
    </>
  );
}
